import {
  ComponentType,
  ErrorOption,
  FormFieldMethods,
  FormTextarea,
  Grid,
  IconName,
  IOption,
  ToastMessage,
  toastMessage,
  UseFormReturn,
} from '@gcl/react-core';
import * as React from 'react';
import { AppConfigContext } from '@config/AppConfig';
import { useHasPermission } from '@config/config.hooks';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { useMutationWithPageTopBarLoadingButton } from '@common/useMutationWithPageTopBarLoadingButton.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IItemResponseWithSuccess } from '@common/api.models';
import { EPermission } from '@userProfile/permission/permission.constants';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { useDeferLoading } from '@common/loading.hooks';
import { EProjectRequestFormKeys, IProjectRequest } from './projectRequest.models';
import { ProjectRequestDetailFormGeneralInfo } from './ProjectRequestDetailFormGeneralInfo';
import { ProjectRequestDetailCompanies } from './ProjectRequestDetailCompanies';
import { useProjectRequestDetailPage } from './projectRequestDetail.hooks';
import {
  useSaveProjectRequestAsDraft,
  useRequestProjectRequestEndorsement,
  useEndorseAndSubmitProjectRequest,
  useApproveProjectRequestAndCreateProject,
} from './projectRequest.queries';
import { IProjectRequestCompany } from './projectRequestCompany/projectRequestCompany.models';
import { IProjectRequestTeam } from './projectRequestTeam/projectRequestTeam.models';
import { IProjectRequestUser } from './projectRequestUser/projectRequestUser.models';
import { EProjectRequestStatus } from './projectRequest.constants';

interface IProjectRequestDetailFormProps {
  isReadOnly: boolean;
  mode: DetailPageAccessMode;
  id?: number;
  projectRequestDetail: IProjectRequest | undefined;
  projectRequestCompanies: IProjectRequestCompany[] | undefined;
  projectRequestTeams: IProjectRequestTeam[] | undefined;
  projectRequestUsers: IProjectRequestUser[] | undefined;
  useFormMethods: UseFormReturn;
  formFieldMethods: FormFieldMethods;
  riskCategoryOptions: IOption<IRiskCategory, string | number, 'key', string | number, 'text'>[] | undefined;
  defaultCompanies: IProjectRequestCompany[] | undefined;
  onSuccessNavigate: (id: number) => void;
  setIsBlockResetDefaultValues: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ProjectRequestDetailForm: React.FunctionComponent<IProjectRequestDetailFormProps> = ({
  isReadOnly,
  mode,
  id,
  projectRequestDetail,
  projectRequestCompanies,
  projectRequestTeams,
  projectRequestUsers,
  useFormMethods,
  formFieldMethods,
  riskCategoryOptions,
  defaultCompanies,
  onSuccessNavigate,
  setIsBlockResetDefaultValues,
}) => {
  const { getRequestObjFromFormValues } = useProjectRequestDetailPage(useFormMethods, riskCategoryOptions);

  const appConfigs = React.useContext(AppConfigContext);
  const userProfileId = appConfigs?.configs?.myUserProfile?.id || 0;
  const isWebMaster = useHasPermission(EPermission.WEB_MASTER);

  const queryClient = useQueryClient();

  const successHandle = (toastMsg: string, result?: IItemResponseWithSuccess<IProjectRequest>) => {
    useFormMethods.reset(useFormMethods.getValues());
    queryClient.invalidateQueries([id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL]);
    queryClient.invalidateQueries([id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL_TEAMS]);
    queryClient.invalidateQueries([id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL_COMPANIES]);
    queryClient.invalidateQueries([id, FETCH_QUERIES_NAME.PROJECT_REQUEST_DETAIL_USERS]);
    toastMessage(
      <ToastMessage message={toastMsg} toastOptions={{ type: 'success', closeOnClick: true, autoClose: 3000 }} />,
    );

    if (mode === 'Create' && result?.item) {
      onSuccessNavigate(result?.item?.id);
    }
  };

  const onClickHandleUpsert = React.useMemo(() => {
    return (mutate: UseMutateFunction<IItemResponseWithSuccess<IProjectRequest>, AxiosErrorResponse, any, unknown>) => {
      useFormMethods.clearErrors();
      useFormMethods.handleSubmit((data: any) => {
        const request = getRequestObjFromFormValues(
          data,
          mode,
          projectRequestCompanies,
          projectRequestTeams,
          projectRequestUsers,
          projectRequestDetail,
        );
        setIsBlockResetDefaultValues(true);
        mutate(request);
      })();
    };
  }, [
    id,
    JSON.stringify(projectRequestDetail),
    JSON.stringify(projectRequestTeams),
    JSON.stringify(projectRequestUsers),
    JSON.stringify(projectRequestDetail),
    mode,
  ]);
  const [isAnyActionLoading, setIsAnyActionLoading] = React.useState(false);

  const {
    error: errorSaveProjectRequestAsDraft,
    isLoading: isLoadingSave,
    loadingButtonProps: saveDraftButtonProps,
  } = useMutationWithPageTopBarLoadingButton({
    title: 'Save',
    componentType: ComponentType.Primary,
    onClick: onClickHandleUpsert,
    disable: isAnyActionLoading,
    useMutation: useSaveProjectRequestAsDraft,
    onSuccess: (variables, result) => {
      successHandle('Save request success', result);
    },
    buttonIconName: 'FolderCheck' as IconName,
  });

  const {
    error: errorRequestProjectRequestEndorsement,
    isLoading: isLoadingSaveAndEndorsement,
    loadingButtonProps: saveAndRequseEndorseButtonProps,
  } = useMutationWithPageTopBarLoadingButton({
    title: 'Save and Request Endorsement',
    componentType: ComponentType.Secondary,
    onClick: onClickHandleUpsert,
    disable: isAnyActionLoading,
    useMutation: useRequestProjectRequestEndorsement,
    onSuccess: (variables, result) => {
      successHandle('Save and request endorsement success', result);
    },
    buttonIconName: 'EnvelopeCheck' as IconName,
  });

  const {
    error: errorEndorseAndSubmitProjectRequest,
    isLoading: isLoadingEndorseAndSubmit,
    loadingButtonProps: endorseAndSubmitButtonProps,
  } = useMutationWithPageTopBarLoadingButton({
    title: 'Endorse and Submit to GEMS Webmaster',
    componentType: ComponentType.Success,
    onClick: onClickHandleUpsert,
    disable: isAnyActionLoading,
    useMutation: useEndorseAndSubmitProjectRequest,
    onSuccess: (variables, result) => {
      successHandle('Endorse and submit to GEMS Webmaster success', result);
    },
    buttonIconName: 'Pen' as IconName,
  });

  const {
    error: errorApproveProjectRequestAndCreateProject,
    isLoading: isLoadingApproveAndCreate,
    loadingButtonProps: approveAndCreateProjectButtonProps,
  } = useMutationWithPageTopBarLoadingButton({
    title: 'Approve and Create Project',
    componentType: ComponentType.Success,
    onClick: onClickHandleUpsert,
    disable: isAnyActionLoading,
    useMutation: useApproveProjectRequestAndCreateProject,
    onSuccess: (variables, result) => {
      successHandle('Approve and create project success', result);
    },
    buttonIconName: 'Pen' as IconName,
  });

  const { loading: deferredIsAnyActionLoading, setLoading: setDeferredIsAnyActionLoading } = useDeferLoading(
    isLoadingSave || isLoadingSaveAndEndorsement || isLoadingEndorseAndSubmit || isLoadingApproveAndCreate,
  );
  React.useEffect(() => {
    setIsAnyActionLoading(deferredIsAnyActionLoading);
  }, [deferredIsAnyActionLoading]);

  const upsertErrors = React.useMemo(
    () => [
      { error: errorSaveProjectRequestAsDraft, genericError: 'Save request failed' },
      { error: errorRequestProjectRequestEndorsement, genericError: 'Save and request endorsement failed' },
      { error: errorEndorseAndSubmitProjectRequest, genericError: 'Endorse and submit to GEMS Webmaster failed' },
      { error: errorApproveProjectRequestAndCreateProject, genericError: 'Approve and create project failed' },
    ],
    [
      errorSaveProjectRequestAsDraft,
      errorRequestProjectRequestEndorsement,
      errorEndorseAndSubmitProjectRequest,
      errorApproveProjectRequestAndCreateProject,
    ],
  );

  const hasCreateNewRequestRight = true; // All Gammon internal and external user can create new project request

  const hasSaveChangesRight = React.useMemo(() => {
    if (!projectRequestUsers || projectRequestUsers.length <= 0 || userProfileId <= 0) return false;
    return projectRequestUsers.some((user) => user.userProfileId === userProfileId);
  }, [JSON.stringify(projectRequestUsers), userProfileId]);

  const hasSubmitRight = React.useMemo(() => {
    if (!projectRequestUsers || projectRequestUsers.length <= 0 || userProfileId <= 0) return false;
    return projectRequestUsers.some(
      (user) =>
        (user.role?.name === 'GEMS Project Manager' || user.role?.name === 'Engineering Manager') &&
        user.userProfileId === userProfileId,
    );
  }, [JSON.stringify(projectRequestUsers), userProfileId]);

  const hasReviseEndorsedRight = React.useMemo(() => {
    if (!projectRequestUsers || projectRequestUsers.length <= 0 || userProfileId <= 0) return false;
    return projectRequestUsers.some(
      (user) => user.role?.name === 'User Rights Admin' && user.userProfileId === userProfileId,
    );
  }, [JSON.stringify(projectRequestUsers), userProfileId]);

  const topBarLeftItems = React.useMemo(() => {
    if (projectRequestDetail && projectRequestDetail.status === EProjectRequestStatus.PROCESSED) return [];

    const temp = [];

    if (
      (!projectRequestDetail && hasCreateNewRequestRight) ||
      (projectRequestDetail &&
        ((projectRequestDetail.status === EProjectRequestStatus.DRAFT && hasSaveChangesRight) ||
          ((projectRequestDetail.status === EProjectRequestStatus.ENDORSED ||
            projectRequestDetail.status === EProjectRequestStatus.ENDORSED_AND_REVISED) &&
            hasReviseEndorsedRight)))
    ) {
      if (!isReadOnly) temp.push(saveDraftButtonProps);
      if (!projectRequestDetail || projectRequestDetail?.status === EProjectRequestStatus.DRAFT)
        temp.push(saveAndRequseEndorseButtonProps);
    }

    if (projectRequestDetail && projectRequestDetail.status === EProjectRequestStatus.DRAFT && hasSubmitRight)
      temp.push(endorseAndSubmitButtonProps);

    if (
      projectRequestDetail &&
      (projectRequestDetail.status === EProjectRequestStatus.ENDORSED ||
        projectRequestDetail.status === EProjectRequestStatus.ENDORSED_AND_REVISED) &&
      isWebMaster
    )
      temp.push(approveAndCreateProjectButtonProps);
    return temp;
  }, [
    JSON.stringify(projectRequestDetail),
    hasCreateNewRequestRight,
    hasSaveChangesRight,
    hasReviseEndorsedRight,
    hasSubmitRight,
    isWebMaster,
    saveDraftButtonProps,
    saveAndRequseEndorseButtonProps,
    endorseAndSubmitButtonProps,
    approveAndCreateProjectButtonProps,
    isReadOnly,
  ]);

  return (
    <>
      <ApiErrorDisplay
        setFormError={(
          name: string,
          error: ErrorOption,
          options?:
            | {
                shouldFocus: boolean;
              }
            | undefined,
        ) => {
          useFormMethods.setError(name, error, options);
        }}
        errors={upsertErrors}
      />
      <DetailPageTopBar
        leftItems={topBarLeftItems as (IDetailPageTopBarItem | React.ReactElement)[]}
        isReadOnly={isReadOnly}
      />

      <Grid container spacing={1.5} columnSpacing={2}>
        <ProjectRequestDetailFormGeneralInfo
          isReadOnly={isReadOnly}
          mode={mode}
          id={id || 0}
          formFieldMethods={formFieldMethods}
        />
        <Grid item xs={12}>
          <br />
          <ProjectRequestDetailCompanies mainFormFieldMethods={useFormMethods} isReadOnly={isReadOnly} />
        </Grid>
        <Grid item xs={12}>
          <LabelWithMessage
            labelFor={EProjectRequestFormKeys.REMARK}
            text="If you have additional requests, please kindly input here:"
          />
          <FormTextarea id={EProjectRequestFormKeys.REMARK} formFieldMethods={formFieldMethods} disabled={isReadOnly} />
        </Grid>
      </Grid>
    </>
  );
};
